import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Link, useLocation, useSearch } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";

type Status = "verifying" | "success" | "error";

// Landing page for the link in the verification email. Reads ?token=,
// confirms it against the API, refreshes the cached auth user (so the
// app-wide email-verified gate re-evaluates), then sends the user into
// onboarding.
export default function VerifyEmail() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const token = useMemo(() => new URLSearchParams(search).get("token") || "", [search]);

  const [status, setStatus] = useState<Status>(token ? "verifying" : "error");
  const [message, setMessage] = useState<string>(
    token ? "" : "This page needs a valid verification link.",
  );
  // Guard against React 18 StrictMode double-invoke consuming the token twice.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        await apiRequest("POST", "/api/auth/verify-email", { token });
        // The verify endpoint logs the user in; refresh the cached user so
        // isEmailVerified() flips true and the gates open.
        await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        setStatus("success");
      } catch (error) {
        setStatus("error");
        setMessage(
          error instanceof Error
            ? error.message
            : "This verification link is invalid or has expired.",
        );
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-muted/20 py-10 px-4 flex items-center justify-center">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle>
            {status === "verifying" && "Confirming your email…"}
            {status === "success" && "Email confirmed"}
            {status === "error" && "Verification failed"}
          </CardTitle>
          <CardDescription>
            {status === "verifying" && "Hang tight while we confirm your email address."}
            {status === "success" &&
              "Thanks! Your email is verified. Let's finish setting up your account."}
            {status === "error" && message}
          </CardDescription>
        </CardHeader>
        <CardContent />
        <CardFooter className="flex flex-col gap-3 items-stretch">
          {status === "success" && (
            <Button onClick={() => setLocation("/onboarding")}>Continue</Button>
          )}
          {status === "error" && (
            <>
              <Button onClick={() => setLocation("/check-email")}>
                Resend verification email
              </Button>
              <Link href="/login" className="text-sm text-muted-foreground text-center hover:underline">
                Back to sign in
              </Link>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
